import {
  Image, View, Text, FlatList, StyleSheet, RefreshControl,
  ScrollView, TouchableOpacity,
} from 'react-native';
import { useState } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useWorldCupGames } from '@/hooks/useWorldCupGames';
import { useWorldCupStandings } from '@/hooks/useWorldCupStandings';
import { MatchCard } from '@/components/MatchCard';
import { SectionHeader } from '@/components/SectionHeader';
import { EmptyState } from '@/components/EmptyState';
import { Colors } from '@/constants/colors';

const COPA_GOLD = '#f59e0b';
const COPA_GOLD_DARK = '#d97706';

type Tab = 'jogos' | 'grupos';

export default function CopaScreen() {
  const { days, liveGames, loading: gamesLoading, error: gamesError, refresh: refreshGames, lastUpdated, source } = useWorldCupGames();
  const { groups, loading: standLoading, error: standError, refresh: refreshStandings } = useWorldCupStandings();

  const [tab, setTab] = useState<Tab>('jogos');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const activeDate = selectedDate ?? (days.find((d) => d.date === today)?.date ?? days[0]?.date ?? null);
  const activeDay = days.find((d) => d.date === activeDate);
  const hasLive = liveGames.length > 0;

  const loading = tab === 'jogos' ? gamesLoading : standLoading;

  function handleRefresh() {
    if (tab === 'jogos') refreshGames();
    else refreshStandings();
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>🏆 Copa do Mundo</Text>
          <Text style={styles.subtitle}>FIFA World Cup 2026</Text>
        </View>
        <View style={styles.headerRight}>
          {lastUpdated && (
            <Text style={styles.updated}>
              {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          )}
          {source && (
            <Text style={styles.sourceTag}>{source === 'espn' ? 'ESPN' : 'API'}</Text>
          )}
        </View>
      </View>

      {/* Jogos ao vivo banner */}
      {hasLive && tab === 'jogos' && (
        <View style={styles.liveBanner}>
          <Text style={styles.liveBannerText}>
            🔴 {liveGames.length} JOGO{liveGames.length > 1 ? 'S' : ''} AO VIVO
          </Text>
        </View>
      )}

      {/* Abas Jogos / Grupos */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tabBtn, tab === 'jogos' && styles.tabBtnActive]}
          onPress={() => setTab('jogos')}
        >
          <Text style={[styles.tabLabel, tab === 'jogos' && styles.tabLabelActive]}>JOGOS</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabBtn, tab === 'grupos' && styles.tabBtnActive]}
          onPress={() => setTab('grupos')}
        >
          <Text style={[styles.tabLabel, tab === 'grupos' && styles.tabLabelActive]}>GRUPOS</Text>
        </TouchableOpacity>
      </View>

      {tab === 'jogos' ? (
        <>
          {/* Seletor de datas */}
          {days.length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.dateScroll}
              contentContainerStyle={styles.dateContent}
            >
              {days.map((day) => {
                const isActive = day.date === activeDate;
                const hasLiveToday = day.games.some((g) => g.status === 'live' || g.status === 'halftime');
                return (
                  <TouchableOpacity
                    key={day.date}
                    style={[styles.dateChip, isActive && styles.dateChipActive]}
                    onPress={() => setSelectedDate(day.date)}
                  >
                    <Text style={[styles.dateLabel, isActive && styles.dateLabelActive]}>
                      {hasLiveToday ? '🔴 ' : ''}{day.label}
                    </Text>
                    <Text style={[styles.gameCount, isActive && styles.gameCountActive]}>
                      {day.games.length} {day.games.length === 1 ? 'jogo' : 'jogos'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}

          {/* Lista de jogos */}
          <FlatList
            data={[]}
            renderItem={null}
            ListHeaderComponent={
              <>
                {gamesError && (
                  <View style={styles.errorBanner}>
                    <Text style={styles.errorText}>⚠️ {gamesError}</Text>
                  </View>
                )}

                {activeDay && activeDay.games.filter((g) => g.status === 'live' || g.status === 'halftime').length > 0 && (
                  <>
                    <SectionHeader
                      title="Ao Vivo"
                      count={activeDay.games.filter((g) => g.status === 'live' || g.status === 'halftime').length}
                      accent={Colors.live}
                    />
                    {activeDay.games
                      .filter((g) => g.status === 'live' || g.status === 'halftime')
                      .map((game) => <MatchCard key={game.id} game={game} />)}
                  </>
                )}

                {activeDay && activeDay.games.filter((g) => g.status === 'scheduled').length > 0 && (
                  <>
                    <SectionHeader
                      title="Programados"
                      count={activeDay.games.filter((g) => g.status === 'scheduled').length}
                      accent={COPA_GOLD}
                    />
                    {activeDay.games
                      .filter((g) => g.status === 'scheduled')
                      .map((game) => <MatchCard key={game.id} game={game} />)}
                  </>
                )}

                {activeDay && activeDay.games.filter((g) => g.status === 'finished').length > 0 && (
                  <>
                    <SectionHeader
                      title="Encerrados"
                      count={activeDay.games.filter((g) => g.status === 'finished').length}
                    />
                    {activeDay.games
                      .filter((g) => g.status === 'finished')
                      .map((game) => <MatchCard key={game.id} game={game} />)}
                  </>
                )}

                {!gamesLoading && days.length === 0 && (
                  <EmptyState icon="🏆" title="Aguardando jogos" subtitle="Os jogos da Copa do Mundo 2026 aparecerão aqui" />
                )}
                {!gamesLoading && activeDay && activeDay.games.length === 0 && (
                  <EmptyState icon="📅" title="Sem jogos neste dia" subtitle="Selecione outra data para ver os jogos" />
                )}
              </>
            }
            refreshControl={
              <RefreshControl refreshing={gamesLoading} onRefresh={refreshGames} tintColor={COPA_GOLD} />
            }
            contentContainerStyle={styles.list}
          />
        </>
      ) : (
        /* ─── Aba Grupos ─────────────────────────────────────────────── */
        <FlatList
          data={groups}
          keyExtractor={(g) => g.id}
          renderItem={({ item: group }) => (
            <View style={styles.groupCard}>
              <Text style={styles.groupName}>{group.name}</Text>

              {/* Cabeçalho da tabela */}
              <View style={styles.tableHeader}>
                <Text style={[styles.thCell, { flex: 3 }]}>Time</Text>
                <Text style={styles.thCell}>J</Text>
                <Text style={styles.thCell}>V</Text>
                <Text style={styles.thCell}>E</Text>
                <Text style={styles.thCell}>D</Text>
                <Text style={styles.thCell}>GD</Text>
                <Text style={[styles.thCell, styles.thPoints]}>PTS</Text>
              </View>

              {group.teams.map((team, i) => (
                <View
                  key={team.teamId}
                  style={[
                    styles.tableRow,
                    i < 2 && styles.qualifiedRow,
                    i === group.teams.length - 1 && styles.lastRow,
                  ]}
                >
                  <View style={[styles.tdCell, { flex: 3 }]}>
                    <Text style={styles.rankNum}>{team.rank}</Text>
                    <Image source={{ uri: team.teamLogo }} style={styles.teamFlag} resizeMode="contain" />
                    <Text style={styles.teamRowName} numberOfLines={1}>{team.teamShortName || team.teamName}</Text>
                  </View>
                  <Text style={styles.tdNum}>{team.played}</Text>
                  <Text style={styles.tdNum}>{team.wins}</Text>
                  <Text style={styles.tdNum}>{team.draws}</Text>
                  <Text style={styles.tdNum}>{team.losses}</Text>
                  <Text style={[styles.tdNum, team.goalDiff > 0 && styles.positive, team.goalDiff < 0 && styles.negative]}>
                    {team.goalDiff > 0 ? '+' : ''}{team.goalDiff}
                  </Text>
                  <Text style={[styles.tdNum, styles.ptsCell]}>{team.points}</Text>
                </View>
              ))}
            </View>
          )}
          ListHeaderComponent={
            standError ? (
              <View style={styles.errorBanner}>
                <Text style={styles.errorText}>⚠️ {standError}</Text>
              </View>
            ) : null
          }
          ListEmptyComponent={
            !standLoading ? (
              <EmptyState
                icon="📊"
                title="Classificação indisponível"
                subtitle="Os grupos aparecerão quando a fase de grupos começar"
              />
            ) : null
          }
          refreshControl={
            <RefreshControl refreshing={standLoading} onRefresh={refreshStandings} tintColor={COPA_GOLD} />
          }
          contentContainerStyle={styles.list}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },

  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  title: { color: Colors.text, fontSize: 22, fontWeight: '800' },
  subtitle: { color: COPA_GOLD, fontSize: 12, fontWeight: '600', marginTop: 2 },
  headerRight: { alignItems: 'flex-end', gap: 4 },
  updated: { color: Colors.textMuted, fontSize: 11 },
  sourceTag: {
    backgroundColor: 'rgba(245,158,11,0.15)',
    color: COPA_GOLD,
    fontSize: 9,
    fontWeight: '700',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },

  liveBanner: {
    marginHorizontal: 16,
    marginVertical: 6,
    padding: 8,
    backgroundColor: 'rgba(239,68,68,0.15)',
    borderRadius: 8,
    alignItems: 'center',
  },
  liveBannerText: { color: '#ef4444', fontSize: 12, fontWeight: '800', letterSpacing: 0.5 },

  tabBar: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: Colors.surface,
    borderRadius: 10,
    padding: 3,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  tabBtnActive: { backgroundColor: COPA_GOLD },
  tabLabel: { color: Colors.textMuted, fontSize: 12, fontWeight: '700', letterSpacing: 0.5 },
  tabLabelActive: { color: '#000' },

  errorBanner: {
    margin: 16,
    padding: 12,
    backgroundColor: 'rgba(239,68,68,0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(239,68,68,0.3)',
  },
  errorText: { color: '#fca5a5', fontSize: 13 },

  dateScroll: { flexGrow: 0 },
  dateContent: { paddingHorizontal: 16, paddingVertical: 10, gap: 8 },
  dateChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: Colors.surface,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
    minWidth: 70,
  },
  dateChipActive: { backgroundColor: COPA_GOLD, borderColor: COPA_GOLD_DARK },
  dateLabel: { color: Colors.textSecondary, fontSize: 13, fontWeight: '700' },
  dateLabelActive: { color: '#000' },
  gameCount: { color: Colors.textMuted, fontSize: 10, marginTop: 2 },
  gameCountActive: { color: 'rgba(0,0,0,0.6)' },

  list: { paddingBottom: 24 },

  // ─── Grupo / Classificação ────────────────────────────────────────────
  groupCard: {
    marginHorizontal: 16,
    marginTop: 14,
    backgroundColor: Colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    overflow: 'hidden',
  },
  groupName: {
    color: COPA_GOLD,
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.5,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: 'rgba(245,158,11,0.1)',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: Colors.surfaceAlt ?? Colors.surface,
  },
  thCell: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: '700',
    width: 28,
    textAlign: 'center',
  },
  thPoints: { color: COPA_GOLD },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  qualifiedRow: { backgroundColor: 'rgba(34,197,94,0.05)' },
  lastRow: {},
  tdCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  rankNum: { color: Colors.textMuted, fontSize: 11, width: 14, textAlign: 'center' },
  teamFlag: { width: 20, height: 14 },
  teamRowName: { color: Colors.text, fontSize: 12, fontWeight: '600', flex: 1 },
  tdNum: {
    color: Colors.textSecondary,
    fontSize: 12,
    width: 28,
    textAlign: 'center',
  },
  positive: { color: '#4ade80' },
  negative: { color: '#f87171' },
  ptsCell: { color: Colors.text, fontWeight: '800', fontSize: 13 },
});
